import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { DOCTOR, whatsappLink } from "@/config/doctorConfig";
import { navLinks } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const hash = href.split("#")[1];
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(hash), 120);
    } else {
      scrollToSection(hash);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/70" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex flex-col leading-tight shrink-0" data-testid="header-logo" onClick={() => setOpen(false)}>
            <span className="font-serif font-bold text-navy-900 text-base sm:text-lg whitespace-nowrap">{DOCTOR.name}</span>
            <span className="text-[10px] sm:text-[11px] font-mono tracking-wider text-teal-700 uppercase whitespace-nowrap">{DOCTOR.qualifications}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2.5">
            <a
              href={DOCTOR.clinicPhoneHref}
              data-testid="header-call-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-navy-900 border border-slate-300 hover:border-teal-600 hover:text-teal-700 transition-colors duration-200"
            >
              <Phone size={15} /> Call Now
            </a>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white bg-[#128C7E] hover:bg-[#0d6e63] transition-colors duration-200"
            >
              <WhatsAppIcon size={16} /> WhatsApp
            </a>
            <a
              href="/#appointment"
              onClick={(e) => go(e, "/#appointment")}
              data-testid="header-book-btn"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white bg-navy-900 hover:bg-navy-700 shadow-soft transition-colors duration-200"
            >
              <CalendarCheck size={15} /> Book Appointment
            </a>
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <a
              href={DOCTOR.clinicPhoneHref}
              data-testid="header-mobile-call-btn"
              aria-label="Call clinic"
              className="p-2.5 rounded-full text-navy-900 border border-slate-300"
            >
              <Phone size={17} />
            </a>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-mobile-whatsapp-btn"
              aria-label="WhatsApp"
              className="p-2.5 rounded-full text-white bg-[#128C7E]"
            >
              <WhatsAppIcon size={17} />
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="header-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              className="p-2.5 rounded-full text-navy-900 border border-slate-300"
            >
              {open ? <X size={17} /> : <Menu size={17} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-slate-200 bg-white" aria-label="Mobile">
          <div className="px-4 py-3 flex flex-col">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-3 px-2 text-base font-medium text-slate-700 border-b border-slate-100 last:border-0"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/#appointment"
              onClick={(e) => go(e, "/#appointment")}
              data-testid="mobile-nav-book-btn"
              className="mt-3 mb-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-navy-900"
            >
              <CalendarCheck size={16} /> Book Appointment
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};
